import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  bookPublicSlot,
  getPublicBooking,
  type PublicBookingMeta,
} from "@/api/meetings";

function dayKey(iso: string): string {
  return iso.slice(0, 10);
}

function formatDay(iso: string): string {
  const d = new Date(iso + "T12:00:00Z");
  return d.toLocaleDateString(undefined, {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
}

function formatSlot(iso: string, timezone: string): string {
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      timeZone: timezone,
    });
  } catch {
    return new Date(iso).toLocaleTimeString();
  }
}

export function BookPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const metaQuery = useQuery({
    queryKey: ["public-book", slug],
    queryFn: () => getPublicBooking(slug),
    enabled: Boolean(slug),
  });

  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<{
    startAt: string;
    endAt: string;
  } | null>(null);
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [confirmed, setConfirmed] = useState<{
    title: string;
    startAt: string;
    conferencingUrl?: string;
  } | null>(null);

  const meta = metaQuery.data as PublicBookingMeta | undefined;

  const days = useMemo(() => {
    if (!meta) return [] as string[];
    const set = new Set(meta.slots.map((s) => dayKey(s.startAt)));
    return [...set].sort();
  }, [meta]);

  const daySlots = useMemo(() => {
    if (!meta || !selectedDay) return [];
    return meta.slots.filter((s) => dayKey(s.startAt) === selectedDay);
  }, [meta, selectedDay]);

  const bookMut = useMutation({
    mutationFn: () => {
      if (!selectedSlot) throw new Error("Select a slot");
      return bookPublicSlot(slug, {
        startAt: selectedSlot.startAt,
        endAt: selectedSlot.endAt,
        guestName,
        guestEmail,
        notes: notes || undefined,
        answers: Object.keys(answers).length ? answers : undefined,
      });
    },
    onSuccess: (res) => {
      setConfirmed({
        title: res.meeting.title,
        startAt: res.meeting.startAt,
        conferencingUrl: res.meeting.conferencingUrl,
      });
    },
  });

  if (metaQuery.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
      </div>
    );
  }

  if (metaQuery.isError || !meta) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <div className="max-w-md text-center">
          <h1 className="text-xl font-semibold">Booking unavailable</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {metaQuery.error instanceof Error
              ? metaQuery.error.message
              : "This link is invalid or inactive."}
          </p>
        </div>
      </div>
    );
  }

  if (confirmed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <div className="w-full max-w-md space-y-3 rounded-lg border border-border p-6 text-center">
          <h1 className="text-xl font-semibold">You&apos;re booked</h1>
          <p className="text-sm text-muted-foreground">{confirmed.title}</p>
          <p className="text-sm">
            {formatSlot(confirmed.startAt, meta.timezone)} · {meta.timezone}
          </p>
          {confirmed.conferencingUrl && (
            <a
              href={confirmed.conferencingUrl}
              className="inline-block text-sm text-brand hover:underline"
            >
              Meeting link
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background px-4 py-10">
      <div className="mx-auto grid w-full max-w-3xl gap-8 md:grid-cols-[1fr_1.1fr]">
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            Book a meeting
          </p>
          <h1 className="mt-1 text-2xl font-semibold">{meta.brandingTitle}</h1>
          {meta.description && (
            <p className="mt-2 text-sm text-muted-foreground">
              {meta.description}
            </p>
          )}
          <p className="mt-4 text-sm text-muted-foreground">
            {meta.durationMin} minutes · {meta.timezone}
          </p>
        </div>

        <div className="space-y-4 rounded-lg border border-border p-4">
          {!selectedSlot ? (
            <>
              <p className="text-sm font-medium">Pick a day</p>
              {days.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No open slots in the next two weeks.
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {days.map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setSelectedDay(d)}
                      className={
                        selectedDay === d
                          ? "rounded-md bg-brand px-3 py-1.5 text-sm text-brand-foreground"
                          : "rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted"
                      }
                    >
                      {formatDay(d)}
                    </button>
                  ))}
                </div>
              )}
              {selectedDay && (
                <>
                  <p className="pt-2 text-sm font-medium">Pick a time</p>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {daySlots.map((s) => (
                      <button
                        key={s.startAt}
                        type="button"
                        onClick={() => setSelectedSlot(s)}
                        className="rounded-md border border-border px-2 py-2 text-sm hover:bg-muted"
                      >
                        {formatSlot(s.startAt, meta.timezone)}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <form
              className="space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                bookMut.mutate();
              }}
            >
              <p className="text-sm">
                <span className="font-medium">
                  {formatSlot(selectedSlot.startAt, meta.timezone)}
                </span>
                <button
                  type="button"
                  className="ml-3 text-xs text-brand hover:underline"
                  onClick={() => setSelectedSlot(null)}
                >
                  Change
                </button>
              </p>
              <label className="block text-sm">
                <span className="text-muted-foreground">Name</span>
                <input
                  required
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2"
                />
              </label>
              <label className="block text-sm">
                <span className="text-muted-foreground">Email</span>
                <input
                  required
                  type="email"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2"
                />
              </label>
              {meta.questions.map((q) => (
                <label key={q.id} className="block text-sm">
                  <span className="text-muted-foreground">{q.label}</span>
                  <input
                    required={q.required}
                    value={answers[q.id] ?? ""}
                    onChange={(e) =>
                      setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))
                    }
                    className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2"
                  />
                </label>
              ))}
              <label className="block text-sm">
                <span className="text-muted-foreground">Notes</span>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2"
                />
              </label>
              {bookMut.isError && (
                <p className="text-sm text-destructive">
                  {bookMut.error instanceof Error
                    ? bookMut.error.message
                    : "Booking failed"}
                </p>
              )}
              <button
                type="submit"
                disabled={bookMut.isPending}
                className="w-full rounded-md bg-brand px-3 py-2 text-sm font-medium text-brand-foreground disabled:opacity-60"
              >
                {bookMut.isPending ? "Booking…" : "Confirm booking"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
